import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Spin } from 'antd';
import { RobotOutlined, CloseOutlined, SendOutlined, CodeOutlined, TableOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'shared/hooks';
import { sendAiChat, AiChatMessage, AiNavItem, AiChatRow } from 'services/api/aiChatService';
import { SIDEBAR_ITEMS, SidebarItem, SubSidebarItem } from 'shared/components/sidebar/constants';
import { EAdminRoles } from 'shared/types';
import QueryResultsModal from './QueryResultsModal';

const SUGGESTED_ADMIN = [
  'What is the average time to complete station 1 for LE 08?',
  'Which teacher has the most students who completed an LE?',
  'What are the top 5 schools by session count?',
  'Which station has the lowest quiz accuracy?',
  'How many students completed all stations of LE 04?',
];

// Teachers only ever see their own school, so cross-school prompts would just
// be misleading — these ask about their own students instead.
const SUGGESTED_TEACHER = [
  'Who are my top 5 most active students?',
  'How many of my students completed all stations of LE 04?',
  'Which station are my students struggling with most?',
  'What is the average time my students spend in a session?',
  'Which of my students have not started yet?',
];

/**
 * The dashboard's own menu, filtered to what this user can actually open, sent
 * with each question so Data Buddy can answer "which report should I go to?".
 * Derived from SIDEBAR_ITEMS at call time rather than described anywhere on
 * the server — add or rename a page and the answers follow automatically.
 */
const buildNavigation = (role?: string): AiNavItem[] => {
  const walk = (items: (SidebarItem | SubSidebarItem)[]): AiNavItem[] =>
    items
      .filter((item) => item.allowedRoles.some((allowed) => allowed === role))
      .map(
        (item): AiNavItem => ({
          title: item.title,
          link: item.link,
          ...(item.subItems?.length ? { subItems: walk(item.subItems) } : {})
        })
      );

  return walk(SIDEBAR_ITEMS);
};

const FAB = styled.button`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  line-height: 1.05;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  box-shadow: 0 4px 16px rgba(24,144,255,0.4);
  z-index: 1000;
  transition: background 0.2s, transform 0.15s;
  &:hover { background: #096dd9; transform: scale(1.08); }
`;

const FabBeta = styled.span`
  font-size: 8px;
  font-weight: 400;
  opacity: 0.85;
`;

const Panel = styled.div<{ open: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 28px;
  width: min(400px, calc(100vw - 56px));
  height: min(560px, calc(100vh - 120px));
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;
  transform: ${({ open }) => open ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(16px)'};
  opacity: ${({ open }) => open ? 1 : 0};
  pointer-events: ${({ open }) => open ? 'all' : 'none'};
  transition: transform 0.2s ease, opacity 0.2s ease;
  transform-origin: bottom right;
`;

const Header = styled.div`
  padding: 14px 16px;
  background: #1890ff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 14px;
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bubble = styled.div<{ $isUser: boolean }>`
  max-width: 88%;
  align-self: ${({ $isUser }) => $isUser ? 'flex-end' : 'flex-start'};
  background: ${({ $isUser }) => $isUser ? '#1890ff' : '#f5f5f5'};
  color: ${({ $isUser }) => $isUser ? '#fff' : '#222'};
  border-radius: 12px;
  padding: 9px 13px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const InlineLink = styled.button`
  display: inline;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: #1890ff;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: #096dd9;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const LinkButton = styled.button`
  font-size: 11px;
  color: #999;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 0;
  text-align: left;
  &:hover { color: #1890ff; }
`;

const SqlBlock = styled.pre`
  background: #f0f0f0;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 4px 0 0;
  color: #333;
  max-width: 100%;
`;

const Suggestions = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const SuggestBtn = styled.button`
  text-align: left;
  background: #f0f5ff;
  border: 1px solid #d6e4ff;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11px;
  color: #1d39c4;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  &:hover { background: #d6e4ff; }
`;

const InputRow = styled.div`
  padding: 10px 12px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 8px;
`;

const MARKDOWN_LINK = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;

/**
 * Renders [label](/path) as a clickable in-app link. Data Buddy answers
 * navigation questions by naming a dashboard page, and a teacher who has just
 * been told where to go should be able to get there in one click rather than
 * retyping a URL. Routing stays inside the SPA so the session is kept.
 */
const renderMessage = (
  content: string,
  onNavigate: (link: string) => void
): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  MARKDOWN_LINK.lastIndex = 0;
  while ((match = MARKDOWN_LINK.exec(content)) !== null) {
    const [full, label, link] = match;
    if (match.index > cursor) parts.push(content.slice(cursor, match.index));
    parts.push(
      <InlineLink key={`${link}-${match.index}`} onClick={() => onNavigate(link)}>
        {label}
      </InlineLink>
    );
    cursor = match.index + full.length;
  }

  if (!parts.length) return content;
  if (cursor < content.length) parts.push(content.slice(cursor));
  return parts;
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sql?: string;
  showSql?: boolean;
  rows?: AiChatRow[];
  rowCount?: number;
}

const GlobalAiChat: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [dataView, setDataView] = useState<Message | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Stable for the life of this panel so the backend can store each exchange
  // as part of one conversation rather than a set of unrelated questions.
  const conversationId = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = useCallback(async (question: string) => {
    if (!question.trim() || loading) return;
    setShowSuggestions(false);
    const userMsg: Message = { role: 'user', content: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history: AiChatMessage[] = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const res = await sendAiChat(
        question,
        history.slice(0, -1), // don't include current question in history
        conversationId.current,
        buildNavigation(user?.role)
      );
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: res.data.answer,
        sql: res.data.sql,
        showSql: false,
        rows: res.data.rows,
        rowCount: res.data.rowCount,
      }]);
    } catch {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble answering that. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, user?.role]);

  // Following a link closes the panel so the page it lands on is visible.
  const handleNavigate = useCallback((link: string) => {
    setOpen(false);
    navigate(link);
  }, [navigate]);

  const toggleSql = useCallback((index: number) => {
    setMessages((prev) => prev.map((m, i) => i === index ? { ...m, showSql: !m.showSql } : m));
  }, []);

  const isSuperAdmin = user?.role === EAdminRoles.SUPER_ADMIN;
  const suggestions = isSuperAdmin ? SUGGESTED_ADMIN : SUGGESTED_TEACHER;

  if (!isAuth || (!isSuperAdmin && user?.role !== EAdminRoles.SCHOOL_ADMIN)) return null;
  if (pathname.includes('/watson-chat')) return null;

  return (
    <>
      <FAB onClick={() => setOpen((v) => !v)} title="Data Buddy — ask questions about your data">
        {open ? (
          <CloseOutlined style={{ fontSize: 22 }} />
        ) : (
          <>
            <span>Data</span>
            <span>Buddy</span>
            <FabBeta>(beta)</FabBeta>
          </>
        )}
      </FAB>

      <Panel open={open}>
        <Header>
          <span><RobotOutlined style={{ marginRight: 8 }} />Data Buddy (beta)</span>
          <Button
            size="small"
            type="text"
            icon={<CloseOutlined />}
            style={{ color: '#fff' }}
            onClick={() => setOpen(false)}
          />
        </Header>

        {showSuggestions && messages.length === 0 && (
          <Suggestions>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>Try asking:</div>
            {suggestions.map((q) => (
              <SuggestBtn key={q} onClick={() => send(q)} disabled={loading}>{q}</SuggestBtn>
            ))}
          </Suggestions>
        )}

        <Messages>
          {messages.length === 0 && !showSuggestions && (
            <div style={{ color: '#bbb', fontSize: 12, textAlign: 'center', marginTop: 24 }}>
              Ask anything about student activity, teachers, or learning outcomes.
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i}>
              <Bubble $isUser={msg.role === 'user'}>
                {msg.role === 'user'
                  ? msg.content
                  : renderMessage(msg.content, handleNavigate)}
              </Bubble>
              {msg.sql && (
                <>
                  <ActionRow>
                    <LinkButton onClick={() => toggleSql(i)}>
                      <CodeOutlined style={{ marginRight: 4 }} />
                      {msg.showSql ? 'Hide SQL' : 'Show SQL'}
                    </LinkButton>
                    {!!msg.rows?.length && (
                      <LinkButton onClick={() => setDataView(msg)}>
                        <TableOutlined style={{ marginRight: 4 }} />
                        View data ({msg.rowCount ?? msg.rows.length} row{(msg.rowCount ?? msg.rows.length) === 1 ? '' : 's'})
                      </LinkButton>
                    )}
                  </ActionRow>
                  {msg.showSql && <SqlBlock>{msg.sql}</SqlBlock>}
                </>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#999', fontSize: 12 }}>
              <Spin size="small" /> Querying data…
            </div>
          )}
          <div ref={bottomRef} />
        </Messages>

        <InputRow>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={() => send(input)}
            placeholder="Ask about students, teachers, LEs…"
            disabled={loading}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => send(input)}
            disabled={!input.trim() || loading}
          />
        </InputRow>
      </Panel>

      <QueryResultsModal
        open={!!dataView}
        rows={dataView?.rows ?? []}
        sql={dataView?.sql}
        rowCount={dataView?.rowCount}
        onClose={() => setDataView(null)}
      />
    </>
  );
};

export default GlobalAiChat;
